/* THE SKILLS PRODUCT INCI DECODER V1 */
(() => {
  if (window.__skillsProductInciDecoderReady) return;
  window.__skillsProductInciDecoderReady = true;

  const CONFIG_SELECTOR = '[data-skills-product-inci-decoder]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 220;

  const normalise = (value = '') =>
    String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[®™*†‡]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const escapeRegExp = (value = '') =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  function findIngredientsAccordion() {
    const candidates = Array.from(
      document.querySelectorAll(
        'details, .product__accordion, .accordion, [class*="collapsible"], [class*="accordion"]'
      )
    );

    return candidates.find((candidate) => {
      const summary =
        candidate.querySelector('summary') ||
        candidate.querySelector('[class*="heading"]') ||
        candidate.querySelector('[class*="title"]');

      return summary && /\bingredients\b/i.test(summary.textContent || '');
    });
  }

  function findIngredientsContent(accordion) {
    if (!accordion) return null;

    return (
      accordion.querySelector('.accordion__content') ||
      accordion.querySelector('.product__accordion-content') ||
      accordion.querySelector('[class*="accordion__content"]') ||
      accordion.querySelector('[class*="collapsible__content"]') ||
      accordion.querySelector('.rte') ||
      Array.from(accordion.children).find(
        (child) => child.tagName.toLowerCase() !== 'summary'
      )
    );
  }

  function buildDictionary(records) {
    const entries = records
      .filter((record) => record?.name)
      .map((record) => ({
        ...record,
        key: normalise(record.name)
      }))
      .sort((a, b) => b.name.length - a.name.length);

    return {
      entries,
      byKey: new Map(entries.map((entry) => [entry.key, entry]))
    };
  }

  function createTooltip() {
    const backdrop = document.createElement('div');
    backdrop.className = 'skills-inci-mobile-sheet-backdrop';

    const tooltip = document.createElement('aside');
    tooltip.className = 'skills-inci-tooltip';
    tooltip.setAttribute('role', 'dialog');
    tooltip.setAttribute('aria-modal', 'false');
    tooltip.setAttribute('aria-hidden', 'true');

    tooltip.innerHTML = `
      <header class="skills-inci-tooltip__header">
        <div class="skills-inci-tooltip__header-copy">
          <span class="skills-inci-tooltip__rating" data-tooltip-rating></span>
          <h2 class="skills-inci-tooltip__title" data-tooltip-title></h2>
        </div>

        <button
          type="button"
          class="skills-inci-tooltip__close"
          data-tooltip-close
          aria-label="Close ingredient information"
        >
          ×
        </button>
      </header>

      <div class="skills-inci-tooltip__body">
        <section class="skills-inci-tooltip__section">
          <span class="skills-inci-tooltip__label">TECHNICAL CLASSIFICATION</span>
          <strong data-tooltip-classification></strong>
        </section>

        <section class="skills-inci-tooltip__section">
          <span class="skills-inci-tooltip__label">PERFORMANCE MECHANICS</span>
          <p data-tooltip-mechanics></p>
        </section>

        <section class="skills-inci-tooltip__section">
          <span class="skills-inci-tooltip__label">KEY SKIN BENEFITS</span>
          <p data-tooltip-benefits></p>
        </section>
      </div>

      <footer class="skills-inci-tooltip__footer">
        <span>THE SKILLS® INCI</span>
        <a class="skills-inci-tooltip__link" href="#" data-tooltip-link>
          VIEW FULL PROFILE →
        </a>
      </footer>
    `;

    document.body.append(backdrop, tooltip);

    return { tooltip, backdrop };
  }

  function positionTooltip(tooltip, trigger) {
    if (isMobile()) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 12;
    const edge = 12;

    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    left = Math.max(edge, Math.min(left, viewportWidth - tooltipRect.width - edge));

    let top = triggerRect.bottom + gap;

    if (top + tooltipRect.height > viewportHeight - edge) {
      top = triggerRect.top - tooltipRect.height - gap;
    }

    if (top < edge) top = edge;

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
  }

  function enhanceContent(content, dictionary, openIngredient) {
    if (!content || content.dataset.skillsInciEnhanced === 'true') return;
    content.dataset.skillsInciEnhanced = 'true';
    content.classList.add('skills-inci-enhanced-list');

    const walker = document.createTreeWalker(
      content,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const parent = node.parentElement;

          if (
            !parent ||
            parent.closest(
              'button, a, script, style, textarea, [data-skills-inci-ignore], .skills-inci-tooltip'
            )
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          const text = node.nodeValue || '';
          const hasPotentialMatch = dictionary.entries.some((entry) =>
            normalise(text).includes(entry.key)
          );

          return hasPotentialMatch
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((textNode) => {
      let remainingText = textNode.nodeValue || '';
      const fragment = document.createDocumentFragment();
      let safety = 0;

      while (remainingText && safety < 500) {
        safety += 1;

        let bestMatch = null;

        dictionary.entries.forEach((entry) => {
          const expression = new RegExp(
            `(^|[\\s,;:/])(${escapeRegExp(entry.name)})(?=$|[\\s,;:.()/%])`,
            'i'
          );
          const match = remainingText.match(expression);

          if (!match) return;

          const nameStart = match.index + match[1].length;

          if (
            !bestMatch ||
            nameStart < bestMatch.start ||
            (nameStart === bestMatch.start &&
              entry.name.length > bestMatch.entry.name.length)
          ) {
            bestMatch = {
              entry,
              start: nameStart,
              length: match[2].length
            };
          }
        });

        if (!bestMatch) {
          fragment.append(document.createTextNode(remainingText));
          remainingText = '';
          break;
        }

        if (bestMatch.start > 0) {
          fragment.append(
            document.createTextNode(remainingText.slice(0, bestMatch.start))
          );
        }

        const originalText = remainingText.slice(
          bestMatch.start,
          bestMatch.start + bestMatch.length
        );

        const term = document.createElement('button');
        term.type = 'button';
        term.className = 'skills-inci-term';
        term.textContent = originalText;
        term.dataset.inciKey = bestMatch.entry.key;
        term.setAttribute('aria-expanded', 'false');
        term.setAttribute(
          'aria-label',
          `Read ingredient information for ${bestMatch.entry.name}`
        );

        term.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openIngredient(term, bestMatch.entry, true);
        });

        fragment.append(term);

        remainingText = remainingText.slice(
          bestMatch.start + bestMatch.length
        );
      }

      textNode.replaceWith(fragment);
    });
  }

  async function initialise() {
    const config = document.querySelector(CONFIG_SELECTOR);
    if (!config) return;

    const dictionaryUrl = config.dataset.inciDictionaryUrl;
    const inciPageUrl = config.dataset.inciPageUrl || '/pages/inci';

    if (!dictionaryUrl) return;

    let response;
    try {
      response = await fetch(dictionaryUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
    } catch (error) {
      console.error('[THE SKILLS PRODUCT INCI] Dictionary request failed.', error);
      return;
    }

    if (!response.ok) {
      console.error('[THE SKILLS PRODUCT INCI] Dictionary could not load.');
      return;
    }

    const records = await response.json();
    const dictionary = buildDictionary(records);
    const { tooltip, backdrop } = createTooltip();

    const rating = tooltip.querySelector('[data-tooltip-rating]');
    const title = tooltip.querySelector('[data-tooltip-title]');
    const classification = tooltip.querySelector(
      '[data-tooltip-classification]'
    );
    const mechanics = tooltip.querySelector('[data-tooltip-mechanics]');
    const benefits = tooltip.querySelector('[data-tooltip-benefits]');
    const profileLink = tooltip.querySelector('[data-tooltip-link]');
    const closeButton = tooltip.querySelector('[data-tooltip-close]');

    let activeTrigger = null;
    let activeEntry = null;
    let openTimer = null;
    let closeTimer = null;

    const setOpenState = (open) => {
      tooltip.classList.toggle('is-visible', open);
      backdrop.classList.toggle('is-visible', open && isMobile());
      tooltip.setAttribute('aria-hidden', open ? 'false' : 'true');

      if (activeTrigger) {
        activeTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      document.documentElement.classList.toggle(
        'skills-inci-sheet-open',
        open && isMobile()
      );
    };

    const closeTooltip = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      setOpenState(false);

      if (activeTrigger) {
        activeTrigger.setAttribute('aria-expanded', 'false');
      }

      activeTrigger = null;
      activeEntry = null;
    };

    const openIngredient = (trigger, entry, immediate = false) => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);

      const reveal = () => {
        if (activeTrigger && activeTrigger !== trigger) {
          activeTrigger.setAttribute('aria-expanded', 'false');
        }

        activeTrigger = trigger;
        activeEntry = entry;

        rating.textContent = entry.rating || 'INCI';
        title.textContent = entry.name;
        classification.textContent =
          entry.classification || 'Ingredient classification';
        mechanics.textContent =
          entry.mechanics || 'Performance information';
        benefits.textContent =
          entry.benefits || 'Supporting skin benefits';

        const query = encodeURIComponent(entry.name);
        profileLink.href = `${inciPageUrl}?ingredient=${query}`;

        setOpenState(true);

        requestAnimationFrame(() => {
          positionTooltip(tooltip, trigger);
        });
      };

      if (immediate || isMobile()) {
        reveal();
      } else {
        openTimer = window.setTimeout(reveal, OPEN_DELAY);
      }
    };

    const scheduleClose = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);

      closeTimer = window.setTimeout(() => {
        const triggerHovered = activeTrigger?.matches(':hover');
        const tooltipHovered = tooltip.matches(':hover');

        if (!triggerHovered && !tooltipHovered) closeTooltip();
      }, CLOSE_DELAY);
    };

    const bindDesktopHover = (container) => {
      container.addEventListener('pointerover', (event) => {
        if (isMobile()) return;

        const term = event.target.closest('.skills-inci-term');
        if (!term || !container.contains(term)) return;

        const entry = dictionary.byKey.get(term.dataset.inciKey);
        if (entry) openIngredient(term, entry, false);
      });

      container.addEventListener('pointerout', (event) => {
        if (isMobile()) return;

        const term = event.target.closest('.skills-inci-term');
        if (!term) return;

        if (term.contains(event.relatedTarget)) return;
        scheduleClose();
      });
    };

    tooltip.addEventListener('pointerenter', () => {
      if (!isMobile()) window.clearTimeout(closeTimer);
    });

    tooltip.addEventListener('pointerleave', () => {
      if (!isMobile()) scheduleClose();
    });

    closeButton.addEventListener('click', closeTooltip);
    backdrop.addEventListener('click', closeTooltip);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeTooltip();
    });

    window.addEventListener('resize', () => {
      if (tooltip.classList.contains('is-visible') && activeTrigger) {
        positionTooltip(tooltip, activeTrigger);
      }
    });

    window.addEventListener(
      'scroll',
      () => {
        if (
          tooltip.classList.contains('is-visible') &&
          activeTrigger &&
          !isMobile()
        ) {
          positionTooltip(tooltip, activeTrigger);
        }
      },
      { passive: true }
    );

    const enhance = () => {
      const accordion = findIngredientsAccordion();
      const content = findIngredientsContent(accordion);

      if (!accordion || !content) return false;

      enhanceContent(content, dictionary, openIngredient);
      bindDesktopHover(content);

      return true;
    };

    if (enhance()) return;

    const observer = new MutationObserver(() => {
      if (enhance()) observer.disconnect();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    window.setTimeout(() => observer.disconnect(), 10000);
  }

  document.addEventListener('DOMContentLoaded', initialise);
})();